import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Plus, Trash2, DollarSign, Building, CreditCard, 
  Home, PiggyBank, Save, Loader2, CheckCircle2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import EmptyState from '@/components/EmptyState';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';

/**
 * FinancialOverviewPage - Bank accounts, credit cards, mortgage, retirement
 * PREMIUM FEATURE - Requires Premium subscription
 */

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking Account', icon: Building },
  { value: 'savings', label: 'Savings Account', icon: PiggyBank },
  { value: 'credit', label: 'Credit Card', icon: CreditCard },
  { value: 'mortgage', label: 'Mortgage', icon: Home },
  { value: 'retirement', label: 'Retirement Account', icon: DollarSign },
  { value: 'investment', label: 'Investment Account', icon: DollarSign },
  { value: 'other', label: 'Other', icon: DollarSign },
];

const FinancialOverviewContent = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getFinancial } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    fetchProfile();
  }, [profileId, isDemoMode]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const demoFinancial = getFinancial() || {};
        setProfile(demoProfile);
        setAccounts(demoFinancial.accounts || []);
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}`);
      setProfile(response.data);
      setAccounts(response.data.financial_accounts || []);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isDemoMode) {
        toast.success('Financial information saved (demo)');
        setSaving(false);
        return;
      }
      
      await API.put(`/emergency-binder/profiles/${profileId}`, {
        financial_accounts: accounts
      });
      toast.success('Financial information saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addAccount = () => {
    setAccounts([...accounts, {
      id: `temp_${Date.now()}`,
      bank_name: '',
      account_type: '',
      notes: ''
    }]);
  };

  const updateAccount = (index, field, value) => {
    setAccounts(accounts.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };

  const removeAccount = (index) => {
    setAccounts(accounts.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="financial-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Financial Overview</h1>
              <p className="text-xs text-slate-500">{profile?.name}</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Privacy Notice */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-amber-800 mb-1">Privacy Notice</h3>
                <p className="text-sm text-amber-700">
                  For security, we recommend NOT storing full account numbers. 
                  Use this section to list institutions and add helpful notes for family members.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Accounts */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-500" />
                Financial Accounts
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addAccount} data-testid="add-account-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Account
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {accounts.length === 0 ? (
              <EmptyState 
                type="financial"
                onAction={addAccount}
                actionLabel="Add First Account"
                showExamples={true}
              />
            ) : (
              accounts.map((account, index) => (
                <div key={account.id || index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Bank name - full width */}
                      <Input
                        placeholder="Bank/Institution name"
                        value={account.bank_name || ''}
                        onChange={(e) => updateAccount(index, 'bank_name', e.target.value)}
                      />
                      {/* Account type - full width */}
                      <Select
                        value={account.account_type || ''}
                        onValueChange={(value) => updateAccount(index, 'account_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Account type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCOUNT_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Notes */}
                      <Textarea
                        placeholder="Notes (optional)"
                        value={account.notes || ''}
                        onChange={(e) => updateAccount(index, 'notes', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => removeAccount(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 bg-amber-600 hover:bg-amber-700"
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Save Financial Information</>
          )}
        </Button>
      </main>
    </div>
  );
};

// Wrap content with feature protection - Premium tier required
const FinancialOverviewPage = () => (
  <ProtectedFeaturePage
    feature="financial_overview"
    title="Financial Overview"
    description="Keep important financial account information organized and accessible for emergencies."
    backPath="/emergency-binder"
  >
    <FinancialOverviewContent />
  </ProtectedFeaturePage>
);

export default FinancialOverviewPage;
